import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, UserPlus } from 'lucide-react';

// Validation Schema
const addDriverSchema = z.object({
  name: z
    .string()
    .min(3, 'الاسم يجب أن يكون 3 أحرف على الأقل')
    .max(100, 'الاسم يجب أن يكون أقل من 100 حرف'),
  phone: z
    .string()
    .min(1, 'رقم الهاتف مطلوب')
    .regex(/^\+?[0-9]{9,15}$/, 'صيغة رقم الهاتف غير صحيحة'),
  email: z
    .string()
    .email('صيغة البريد الإلكتروني غير صحيحة')
    .optional()
    .or(z.literal('')),
  password: z
    .string()
    .min(8, 'كلمة المرور يجب أن تكون 8 أحرف على الأقل'),
  carrier_id: z.string().optional(),
  capacity_id: z.string().optional(),
  payment_type: z.enum(['salary', 'commission']),
  commission_rate: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const num = parseFloat(val);
        return !isNaN(num) && num >= 0 && num <= 100;
      },
      { message: 'نسبة العمولة يجب أن تكون بين 0 و 100' }
    ),
  is_active: z.boolean(),
});

type AddDriverFormValues = z.infer<typeof addDriverSchema>;

interface Carrier {
  id: string;
  name: string;
}

interface Capacity {
  id: string;
  name: string;
  vehicle_type: string;
}

interface AddDriverModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddDriverModal({ open, onOpenChange, onSuccess }: AddDriverModalProps) {
  const { post, get, loading } = useApi();
  const { user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [capacities, setCapacities] = useState<Capacity[]>([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [loadingCapacities, setLoadingCapacities] = useState(false);

  const isAdmin = user?.role === 'admin';

  const form = useForm<AddDriverFormValues>({
    resolver: zodResolver(addDriverSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      password: '',
      carrier_id: '',
      capacity_id: '',
      payment_type: 'salary',
      commission_rate: '',
      is_active: true,
    },
  });

  const paymentType = form.watch('payment_type');

  // Fetch carriers for admin users
  useEffect(() => {
    if (open && isAdmin) {
      fetchCarriers();
    }
  }, [open, isAdmin]);

  // Fetch capacities when modal opens
  useEffect(() => {
    if (open) {
      fetchCapacities();
    }
  }, [open]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      form.reset();
      setShowPassword(false);
    }
  }, [open, form]);

  const fetchCarriers = async () => {
    setLoadingCarriers(true);
    try {
      const response: any = await get('/api/v1/carriers/active');
      if (response?.data?.list && Array.isArray(response.data.list)) {
        setCarriers(response.data.list.map((c: any) => ({ id: c.id, name: c.name })));
      } else if (response?.list && Array.isArray(response.list)) {
        setCarriers(response.list.map((c: any) => ({ id: c.id, name: c.name })));
      }
    } catch (error) {
      console.error('Failed to fetch carriers:', error);
    } finally {
      setLoadingCarriers(false);
    }
  };

  const fetchCapacities = async () => {
    setLoadingCapacities(true);
    try {
      const response: any = await get('/api/v1/capacities');
      if (response?.data && Array.isArray(response.data)) {
        setCapacities(response.data);
      } else if (Array.isArray(response)) {
        setCapacities(response);
      }
    } catch (error) {
      console.error('Failed to fetch capacities:', error);
    } finally {
      setLoadingCapacities(false);
    }
  };

  const onSubmit = async (values: AddDriverFormValues) => {
    try {
      const payload: Record<string, any> = {
        name: values.name.trim(),
        phone: values.phone.trim(),
        password: values.password,
        payment_type: values.payment_type,
        is_active: values.is_active,
      };

      if (values.email) {
        payload.email = values.email.trim();
      }

      if (isAdmin && values.carrier_id) {
        payload.carrier_id = values.carrier_id;
      }

      if (values.capacity_id) {
        payload.capacity_id = values.capacity_id;
      }

      if (values.payment_type === 'commission' && values.commission_rate) {
        // Convert percentage to decimal (15% -> 0.15)
        payload.commission_rate = parseFloat(values.commission_rate) / 100;
      }

      await post('/api/v1/dashboard/drivers', payload);
      
      toast.success('تم إنشاء السائق بنجاح');
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في إنشاء السائق';
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <UserPlus className="h-5 w-5" />
            إضافة سائق جديد
          </DialogTitle>
          <DialogDescription>
            أدخل بيانات السائق الجديد لإضافته إلى النظام
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                المعلومات الأساسية
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Full Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>الاسم الكامل *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="أدخل اسم السائق الكامل"
                          {...field}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Phone Number */}
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>رقم الهاتف *</FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Email */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>البريد الإلكتروني</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="driver@example.com"
                          dir="ltr"
                          className="text-start"
                          {...field}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Password */}
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>كلمة المرور *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="أدخل كلمة مرور آمنة"
                            dir="ltr"
                            className="text-start pe-10"
                            {...field}
                            disabled={loading}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute start-0 top-0 h-full px-3 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                            tabIndex={-1}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Carrier Selection - Admin Only */}
            {isAdmin && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                  الناقل
                </h3>
                <FormField
                  control={form.control}
                  name="carrier_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>اختر الناقل *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={loading || loadingCarriers}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="اختر الناقل" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {carriers.map((carrier) => (
                            <SelectItem key={carrier.id} value={carrier.id}>
                              {carrier.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Vehicle Capacity */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                سعة المركبة
              </h3>
              <FormField
                control={form.control}
                name="capacity_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>نوع السعة</FormLabel>
                    <Select
                      onValueChange={(val) => field.onChange(val === 'none' ? '' : val)}
                      value={field.value || 'none'}
                      disabled={loading || loadingCapacities}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر نوع السعة (اختياري)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">بدون تحديد</SelectItem>
                        {capacities.map((capacity) => (
                          <SelectItem key={capacity.id} value={capacity.id}>
                            {capacity.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Payment Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                معلومات الدفع
              </h3>

              <FormField
                control={form.control}
                name="payment_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>نوع الدفع</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex gap-6"
                        disabled={loading}
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="salary" id="salary" />
                          <Label htmlFor="salary" className="cursor-pointer">
                            راتب ثابت
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="commission" id="commission" />
                          <Label htmlFor="commission" className="cursor-pointer">
                            عمولة
                          </Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Commission Rate - Conditional */}
              {paymentType === 'commission' && (
                <FormField
                  control={form.control}
                  name="commission_rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>نسبة العمولة (%) *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="15"
                          min={0}
                          max={100}
                          step={0.1}
                          dir="ltr"
                          className="text-start"
                          {...field}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormDescription>
                        أدخل النسبة المئوية (مثال: 15 تعني 15%)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Status */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                الحالة
              </h3>

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>نشط</FormLabel>
                      <FormDescription>
                        السائقين غير النشطين لا يمكنهم استلام شحنات جديدة
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={loading}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin ms-2" />
                    جاري الإنشاء...
                  </>
                ) : (
                  'إنشاء السائق'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                إلغاء
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}